import { css } from '@emotion/react'
import { Card, colors, Tooltip } from 'plume-ui'
import ReactTooltip from 'react-tooltip'
import { useState, Fragment } from 'react'
import { FundingRoundData, RoundDetailData } from '../../../../../types/api'
import { CardInnerTitle } from '../../../../atoms/CardInnerTitle'
import {
  Table,
  TableCell,
  TableHead,
  TableHeading,
} from '../../../../atoms/Table'
import { OpenCloseToggle } from '../../../../common/OpenCloseToggle'
import { equal } from '@/lib/collection'
import { A } from '../../../../atoms/A'
import { formatWithComma } from '@/lib/number'
import { LineClampText } from '../../../../atoms/LineClampText'
import { IcomoonIcon } from '../../../../atoms/IcomoonIcon'
import { IGNORED_BY_WOVN_CLASS_NAME } from '@/lib/wovn'
import { FormattedDate } from '../../../../atoms/FormattedDate'

type Props = {
  list: FundingRoundData[]
}

export const CompanyFundingRounds: React.FC<Props> = (props) => {
  const [openingShortnames, setOpeningShortnames] = useState<string[]>([])

  const roundWithDetailsShortnames: string[] = props.list
    .filter((fr) => fr.roundDetails && fr.roundDetails.length > 0)
    .map((fr) => fr.shortname)
  const hasRoundWithDetails = roundWithDetailsShortnames.length !== 0

  const toggleRoundDetails = (shortname: string) => {
    if (isRoundOpen(shortname)) {
      setOpeningShortnames(openingShortnames.filter((s) => s !== shortname))
    } else {
      setOpeningShortnames([...openingShortnames, shortname])
    }
  }

  const toggleAllRoundDetails = () => {
    if (isAllRoundsOpen) {
      setOpeningShortnames([])
      ReactTooltip.hide()
    } else {
      setOpeningShortnames(roundWithDetailsShortnames)
      ReactTooltip.hide()
    }
  }

  const isRoundOpen = (shortname: string) =>
    openingShortnames.includes(shortname)

  const isAllRoundsOpen =
    hasRoundWithDetails && equal(roundWithDetailsShortnames, openingShortnames)
  const buttonLabel = !hasRoundWithDetails
    ? ''
    : isAllRoundsOpen
      ? 'すべて閉じる'
      : 'すべて開く'

  return (
    <Card
      css={css`
        margin-top: 2rem;
      `}
    >
      <CardInnerTitle title="ラウンド情報" />
      <Table>
        <TableHead
          sticky
          offset="112px"
          dataTestId="funding-rounds-table-header"
        >
          <tr>
            <TableHeading>
              <Tooltip label={buttonLabel}>
                <OpenCloseToggle
                  status={
                    isAllRoundsOpen
                      ? 'open'
                      : hasRoundWithDetails
                        ? 'closed'
                        : 'disabled'
                  }
                  onClick={toggleAllRoundDetails}
                  isLarge
                />
              </Tooltip>
            </TableHeading>
            <TableHeading
              css={css`
                padding-left: 0.2rem !important;
              `}
            >
              ラウンド行
            </TableHeading>
            <TableHeading>年月日</TableHeading>
            <TableHeading>種別</TableHeading>
            <TableHeading
              help="スピーダが独自に定義する成長フェーズ"
              align="center"
              helpProps={{
                linkTo: process.env.INITIAL_SERIES_HELP_ENDPOINT,
              }}
            >
              スピーダ
              <br />
              調達シリーズ
            </TableHeading>
            <TableHeading sub="円">株価</TableHeading>
            <TableHeading sub="千円">調達金額</TableHeading>
            <TableHeading />
            <TableHeading sub="千円">調達後評価額</TableHeading>
            <TableHeading>備考</TableHeading>
          </tr>
        </TableHead>
        <tbody>
          {props.list.map((fr, i) => {
            const roundDetails = fr.roundDetails || []

            return (
              <Fragment key={`${i}-${fr.shortname}`}>
                <tr
                  id={fr.shortname}
                  css={css`
                    background-color: ${fr.initialSeries
                      ? colors.cyan10
                      : colors.white00};
                  `}
                >
                  <TableCell
                    css={css`
                      text-align: center;
                      padding-top: 0.9rem;
                      padding-bottom: 0.7rem;
                      padding-left: 0rem;
                      padding-right: 0rem;
                      min-width: 50px;
                    `}
                    className="expand-toggle"
                  >
                    {roundDetails.length > 0 && (
                      <OpenCloseToggle
                        status={isRoundOpen(fr.shortname) ? 'open' : 'closed'}
                        onClick={() => toggleRoundDetails(fr.shortname)}
                      />
                    )}
                  </TableCell>
                  <TableCell
                    nowrap
                    css={css`
                      padding-left: 0.2rem;
                    `}
                    className={IGNORED_BY_WOVN_CLASS_NAME}
                  >
                    <A
                      href={fr.path}
                      target="_blank"
                      rel="noopener"
                      decoration="none"
                    >
                      {fr.shortname}
                    </A>
                  </TableCell>
                  <TableCell nowrap className={IGNORED_BY_WOVN_CLASS_NAME}>
                    {fr.date && <FormattedDate>{fr.date}</FormattedDate>}
                  </TableCell>
                  <TableCell nowrap>{fr.description}</TableCell>
                  <TableCell>{fr.initialSeries}</TableCell>
                  <TableCell
                    align="right"
                    className={IGNORED_BY_WOVN_CLASS_NAME}
                  >
                    {formatWithComma(fr.pricePerShare, { decimalPlace: 1 })}
                  </TableCell>
                  <TableCell
                    align="right"
                    className={IGNORED_BY_WOVN_CLASS_NAME}
                  >
                    {formatWithComma(fr.amount)}
                  </TableCell>
                  <EstimatedCell estimated={fr.estimated} />
                  <TableCell
                    align="right"
                    className={IGNORED_BY_WOVN_CLASS_NAME}
                  >
                    {formatWithComma(fr.postMoney)}
                  </TableCell>
                  <TableCell>
                    <LineClampText lineNumber={1}>{fr.remarks}</LineClampText>
                  </TableCell>
                </tr>
                {roundDetails.length > 0 && isRoundOpen(fr.shortname) && (
                  <tr>
                    <td
                      colSpan={10}
                      css={css`
                        padding: 0;
                      `}
                    >
                      <div>
                        <RoundDetailTable list={roundDetails} />
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            )
          })}
        </tbody>
      </Table>
    </Card>
  )
}

const RoundDetailTable: React.FC<{ list: RoundDetailData[] }> = (props) => {
  return (
    <Table
      css={css`
        padding-left: 30px;
        background-color: ${colors.white10};
        th {
          background-color: ${colors.white10};
        }
      `}
    >
      <thead
        css={css`
          th {
            top: 100px;
            font-size: 12px;
            color: ${colors.textLight};
          }
        `}
      >
        <tr>
          <TableHeading
            css={css`
              width: 15%;
              padding-left: 22px !important;
            `}
          >
            ラウンド行
          </TableHeading>
          <TableHeading
            css={css`
              width: 15%;
            `}
          >
            投資家名
          </TableHeading>
          <TableHeading
            css={css`
              width: 15%;
            `}
          >
            株式種類
          </TableHeading>
          <TableHeading
            css={css`
              width: 15%;
            `}
          >
            投資額(千円){' '}
          </TableHeading>
          <TableHeading />
          <TableHeading
            css={css`
              width: 40%;
            `}
          >
            備考
          </TableHeading>
        </tr>
      </thead>
      <tbody>
        {props.list.map((rd, i) => {
          const isLast = props.list.length === i + 1

          return (
            <tr
              key={i}
              css={css`
                td {
                  border-bottom-width: ${isLast ? 0 : '1px'};
                }
              `}
            >
              <TableCell
                css={css`
                  padding-left: 18px !important;
                `}
                className={`${IGNORED_BY_WOVN_CLASS_NAME} round-shortname`}
              >
                <LowerLeftIcon />
                <A
                  href={rd.path}
                  target="_blank"
                  rel="noopener"
                  decoration="none"
                >
                  {rd.shortname}
                </A>
              </TableCell>
              <TableCell className="investor-name">
                {rd.gpInvestors && (
                  <A
                    href={rd.gpInvestors[0].path}
                    decoration="none"
                    target="_blank"
                    rel="noopener"
                  >
                    {rd.gpInvestors[0].name}
                  </A>
                )}
              </TableCell>
              <TableCell>{rd.typeOfStock}</TableCell>
              <TableCell align="right" className={IGNORED_BY_WOVN_CLASS_NAME}>
                {formatWithComma(rd.amount)}
              </TableCell>
              <EstimatedCell estimated={rd.estimated} />
              <TableCell>
                <LineClampText lineNumber={2}>{rd.remarks}</LineClampText>
              </TableCell>
            </tr>
          )
        })}
      </tbody>
    </Table>
  )
}

const LowerLeftIcon: React.FC = () => {
  return (
    <IcomoonIcon
      css={css`
        padding-right: 5px;
        padding-left: 3px;
      `}
      name="box-drawing-character"
    ></IcomoonIcon>
  )
}

const EstimatedCell: React.FC<{ estimated: boolean }> = (props) => {
  return (
    <TableCell
      css={css`
        color: ${colors.textLight};
        font-size: 80%;
        padding-left: 0;
      `}
      nowrap
    >
      {props.estimated && '(推測)'}
    </TableCell>
  )
}
